import { useEffect, useState } from "react";
import { Box } from "@chakra-ui/react";
import NestedTable from "../../../components/NestedTable";
import Pagination from "../../../components/Pagination";
import useSyncPagination from "../../../components/Pagination/hooks/useSyncPagination";
import Table from "../../../components/Table";
import { TableData } from "../../../components/Table/types";
import { Schema, SchemaColumn } from "../../../api/types";
import usePostSchemaColumn from "../../../api/usePostSchemaColumn";
import { Update } from "../../../hooks/useEntitySelection";
import { typedSxMap } from "../../../utils/typedSxMap";
import { nestedColumnsTable } from "../utils/nestedColumnsTable";

type ColumnsTableProps = {
  schema: Schema;
  dataFiltered: SchemaColumn[];
  update: Update;
  setColumns: (columns: SchemaColumn[]) => void;
};

const styles = typedSxMap({
  container: {
    width: "100%",
    ".header": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      margin: "0 0 var(--m) 0",
    },
    "[role='table']": {
      height: "100%",
    },
  },
  info: {
    display: "flex",
    padding: "var(--m-xs)",
    gap: "var(--m-xs)",
    "a.infoTitle": {
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    ".iconPanel": {
      svg: {
        width: "18px",
        height: "18px",
        objectFit: "contain",
      },
      "&.select svg path": {
        stroke: "var(--color-primary)",
      },
      "&.insert svg path": {
        stroke: "var(--color-green)",
      },
      "&.update svg": {
        transform: "rotate(90deg)",
        path: {
          stroke: "var(--color-yellow)",
        },
      },
      "&.delete svg path": {
        stroke: "var(--color-red)",
      },
      "&.database svg path": {
        stroke: "var(--color-text)",
      },
    },
    ".textsPanel": {
      flexShrink: 1,
      maxWidth: "100%",
    },
    h2: {
      fontSize: "inherit",
      fontWeight: 600,
      marginBottom: "var(--m-xxxxs)",
      display: "block",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
      letterSpacing: "0.01em",
    },
    ".footer": {
      display: "flex",
      gap: "var(--m-xs)",
      color: "var(--color-text-soft)",
      fontSize: "var(--font-size-s)",
      svg: {
        path: {
          stroke: "var(--color-text-soft)",
        },
      },
      "& > *": {
        display: "flex",
        gap: "var(--m-xxxxs)",
        alignItems: "center",
      },
      ".author": {
        color: "var(--color-blue)",
      },
    },
  },
  extraWide: {
    maxWidth: "870px !important",
  },
});

export default function SchemaColumnsTable({ schema, update, dataFiltered, setColumns }: ColumnsTableProps) {
  const { mutate, isLoading, error, isSuccess } = usePostSchemaColumn(schema.id);
  const [tableData, setTableData] = useState<TableData>([]);

  const itemsPerPage = 50;
  const { dataPage, page, paginate, totalItems } = useSyncPagination(dataFiltered as any, itemsPerPage);

  const handleRowsReorder = async (data: any) => {
    const templateColumnId = data?.draggableId;
    const newIndex = data?.destination?.index;

    if (newIndex === undefined || templateColumnId === undefined || !dataFiltered) {
      return;
    }

    // Create a copy of the columns array to manipulate
    const updatedColumns = [...dataFiltered];

    // Find the column that is being moved
    const movingColumn = updatedColumns.find((column) => column.id === templateColumnId);
    if (!movingColumn) {
      return; // Return if the moving column is not found
    }

    // Remove the moving column from its original position
    updatedColumns.splice(updatedColumns.indexOf(movingColumn), 1);

    // Insert the moving column in its new position
    updatedColumns.splice(newIndex, 0, movingColumn);

    // Normalize indexes so they are sequential starting from 0
    const normalizedColumns = updatedColumns.map((column, index) => {
      return { ...column, index };
    });

    // Update the state with the new columns order
    setTableData(nestedColumnsTable(normalizedColumns as any, update));
    await mutate({ id: templateColumnId, index: newIndex });
    setColumns(normalizedColumns);
  };

  useEffect(() => {
    setTableData(nestedColumnsTable(dataPage as any, update));
  }, [dataPage]);
  const hasDescription = tableData?.[0]?.hasOwnProperty("Description");
  const columnWidths = hasDescription ? ["4%", "23%", "35%", "20%", "13%", "5%"] : ["4%", "37%", "41%", "13%", "5%"];

  return (
    <Box sx={styles.container}>
      {tableData && (
        <NestedTable
          data={tableData}
          reorderable={true}
          onRowsReorder={handleRowsReorder}
          columnWidths={columnWidths}
          hideColumns={["_id", "_index"]}
          emptyState={<Box>No columns found</Box>}
          keyAsId="_id"
        />
      )}

      {!!(totalItems && totalItems > itemsPerPage) && (
        <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} onPageChange={paginate} initialPage={page} />
      )}
    </Box>
  );
}
